import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { fonts } from '@helpers/fonts';
import { Device, from, until } from '@helpers/media';
import { ButtonStyles } from '@stories/Components/Buttons/Button/Button.styles';
import { Containers } from '@stories/Widgets/YourSolution/YS/Components/Shared/Step.styles';
import { m } from 'framer-motion';
import { transparentize } from 'polished';
import styled from 'styled-components';
const Container = styled(Containers.FullWidth) `
  margin-top: ${fluid(34, 48)};

  display: flex;
  flex-direction: column;
`;
const TitleContainer = styled.div `
  text-align: center;
  padding-bottom: ${fluid(24, 86)};

  display: flex;
  flex-direction: column;
  gap: ${fluid(20, 31)};

  h1 {
    ${fonts.DaxPro.Bold};
    color: ${brand.primary.alt};
    font-size: ${fluid(26, 35)};
    line-height: ${fluid(32, 45)};
    margin: 0;
  }

  p {
    ${fonts.DaxPro.Regular};
    color: ${brand.grey.grey40};
    font-size: ${fluid(18, 20)};
    line-height: ${fluid(22, 25)};
    margin: 0;
  }
`;
const FiltersContainer = styled.div `
  display: flex;
  flex-direction: column;
  gap: ${fluid(25, 32)};
  z-index: 5;
`;
const FiltersControls = styled.div `
  display: flex;
  justify-content: space-between;
`;
const FiltersLeft = styled.div `
  display: flex;
  align-items: center;
  gap: 16px;

  > span {
    ${fonts.DaxPro.Bold};
    margin-right: ${fluid(0, 13, Device.Desktop)};
  }
`;
const FiltersRight = styled.div `
  display: flex;
  align-items: center;
  gap: 16px;

  > span {
    @media ${until(Device.DesktopLarge)} {
      display: none;
    }

    ${fonts.DaxPro.Bold};
    margin-left: 26px;
  }
`;
const MultiSelect = styled.div `
  position: relative;
  display: flex;
  flex-direction: column;

  @media ${from(Device.TabletLarge)} {
    min-width: 236px;
  }

  opacity: 1;
  &[data-disabled='true'] {
    opacity: 0.75;
    pointer-events: none;
  }

  transition: opacity 0.25s ease-in-out;
`;
const MultiSelectWrapper = styled.div `
  padding: ${fluid(10, 13)} ${fluid(16, 23)};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${brand.white};
  border: 1px solid ${brand.primary.alt};
  border-radius: 8px;
  cursor: pointer;
  z-index: 1;

  &[data-mobile-trigger] {
    svg {
      transform: rotate(180deg);
    }

    gap: 10px;
  }

  &[aria-expanded='true'] {
    svg {
      transform: rotate(180deg);
    }
  }

  > span {
    ${fonts.DaxPro.Bold};
    font-size: 18px;
    line-height: 23px;
  }

  svg {
    width: 14px;
    height: 14px;
    transition: transform 0.3s;
  }
`;
const MultiSelectOptions = styled(m.div).attrs((props) => ({
    initial: 'hidden',
    animate: props['data-visible'] ? 'visible' : 'hidden',
    variants: {
        hidden: {
            height: 0,
            opacity: 0,
            pointerEvents: 'none',
            overflow: 'hidden',
        },
        visible: {
            height: 'max-content',
            opacity: 1,
            pointerEvents: 'all',
            overflow: 'auto',
        },
    },
    transition: {
        bounce: 0,
    },
})) `
  --height: 51px;
  position: absolute;
  inset: 0;
  width: 100%;
  padding-left: 10px;
  padding-right: 10px;
  padding-top: calc(var(--height) + 16px);
  padding-bottom: 16px;
  background-color: ${brand.white};
  z-index: 0;

  display: flex;
  flex-direction: column;
  gap: 22px;

  border-radius: 0px 0px 8px 8px;

  span {
    ${fonts.DaxPro.Medium};
    font-size: ${fluid(16, 18)};
    line-height: ${fluid(20, 23)};
  }

  div {
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 12px;
  }
`;
const MultiSelectCheck = styled.div `
  width: 20px;
  height: 20px;
  position: relative;
  border: 1px solid ${brand.grey.grey44};
  flex-shrink: 0;

  &[data-selected='true'] {
    svg {
      opacity: 1;
    }
  }

  svg {
    opacity: 0;
    transition: opacity 0.25s ease-in-out;
    width: 16px;
    height: 16px;
    margin: auto;

    path {
      fill: ${brand.primary.alt} !important;
    }
  }
`;
const Pills = styled.div `
  display: flex;
  gap: ${fluid(8, 16)};
  flex-wrap: wrap;

  @media ${until(Device.TabletLarge)} {
    margin-bottom: ${fluid(17, 32)};
    flex-wrap: nowrap;
    overflow: auto;
  }
`;
const Pill = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 6px;
  background: ${brand.primary.alt};
  color: ${brand.white};
  padding: 4px 12px;
  ${fonts.DaxPro.Medium};
  font-size: ${fluid(16, 18)};
  line-height: ${fluid(20, 23)};
  cursor: pointer;
  gap: ${fluid(8, 15)};
  white-space: nowrap;
  min-width: max-content;

  svg {
    width: 13px;
    height: 13px;
    color: ${brand.white};

    @media ${until(Device.TabletLarge)} {
      width: 11px;
      height: 11px;

      g {
        stroke-width: 4px;
      }
    }
  }
`;
const Cards = styled(m.div).attrs(() => ({
    transition: {
        staggerChildren: 1,
    },
})) `
  display: grid;
  column-gap: 30px;
  row-gap: ${fluid(32, 72)};
  padding-top: ${fluid(25, 56)};
  padding-bottom: ${fluid(32, 72)};

  grid-template-columns: 1fr;
  @media ${from(Device.Tablet)} {
    grid-template-columns: repeat(2, 1fr);
  }
  @media ${from(Device.TabletLarge)} {
    grid-template-columns: repeat(3, 1fr);
  }
  @media ${from(Device.DesktopLarge)} {
    grid-template-columns: repeat(4, 1fr);
  }
`;
const CardWrapper = styled(m.a).attrs((props) => ({
    initial: 'hidden',
    animate: props['data-in-view'] ? 'visible' : 'hidden',
    exit: 'hidden',
    variants: {
        hidden: {
            opacity: 0,
        },
        visible: {
            opacity: 1,
        },
    },
})) `
  text-decoration: none;
  display: block;
  border-top: 2px solid ${brand.primary.alt};

  &:hover {
    ${() => CardContentTitle} {
      text-decoration: underline;
    }
  }
`;
const CardInner = styled.div `
  display: flex;
  flex-direction: column;
  position: relative;
  height: 100%;
  overflow: hidden;
`;
const ImageWrapper = styled.div `
  height: ${fluid(204, 245)};
  overflow: hidden;
  position: relative;
  flex-shrink: 0;

  background: ${transparentize(0.9, brand.primary.alt)};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center center;
  }
`;
const LazyImage = styled(m.img).attrs((props) => ({
    initial: 'hidden',
    animate: props['data-loaded'] ? 'visible' : 'hidden',
    variants: {
        hidden: {
            opacity: 0,
            scale: 0.95,
        },
        visible: {
            opacity: 1,
            scale: 1,
        },
    },
})) ``;
const SpinnerContainer = styled(m.div).attrs(() => ({
    initial: 'hidden',
    animate: 'visible',
    exit: 'hidden',
    variants: {
        hidden: {
            opacity: 0,
            scale: 0.5,
        },
        visible: {
            opacity: 1,
            scale: 1,
        },
    },
})) `
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const Date = styled(m.div).attrs(() => ({
    variants: {
        hidden: {
            x: '100%',
        },
        visible: {
            x: '0%',
        },
    },
    transition: {
        duration: 0.45,
        bounce: 0,
        ease: 'easeInOut',
    },
})) `
  ${fonts.DaxPro.Bold};
  position: absolute;
  top: 0;
  right: 0;
  padding: 8px 16px;
  background: ${brand.primary.alt};
  color: ${brand.white};
  font-size: 23px;
  line-height: 29px;
  z-index: 2;
`;
const CardContent = styled(m.div).attrs(() => ({
    variants: {
        visible: {
            transition: {
                staggerChildren: 0.15,
            },
        },
    },
})) `
  display: flex;
  flex-direction: column;
  position: relative;
  margin-top: 16px;
  padding-left: 18px;
  flex-grow: 1;
`;
const CardContentLine = styled(m.div).attrs(() => ({
    variants: {
        hidden: {
            height: '0px',
        },
        visible: {
            height: '100%',
        },
    },
    transition: {
        duration: 0.7,
        ease: 'easeOut',
    },
})) `
  position: absolute;
  inset: 0;
  background: ${brand.primary.alt};
  width: 2px;
`;
const CardContentTitle = styled(m.h3).attrs(() => ({
    variants: {
        hidden: {
            opacity: 0,
            y: '25px',
        },
        visible: {
            opacity: 1,
            y: '0px',
        },
    },
})) `
  margin: 0;
  ${fonts.DaxPro.Bold};
  font-size: ${fluid(18, 21)};
  line-height: 25px;
  color: ${brand.black};
  margin-bottom: ${fluid(13, 20)};
`;
const CardContentIntro = styled(m.p).attrs(() => ({
    variants: {
        hidden: {
            opacity: 0,
            y: '25px',
        },
        visible: {
            opacity: 1,
            y: '0px',
        },
    },
})) `
  margin: 0;
  ${fonts.DaxPro.Regular};
  font-size: ${fluid(16, 18)};
  line-height: 25px;
  color: ${brand.black};
  margin-top: auto;

  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  @media ${from(Device.TabletLarge)} {
    -webkit-line-clamp: 2;
  }
`;
const CardContentCta = styled(m.span).attrs(() => ({
    variants: {
        hidden: {
            opacity: 0,
            y: '25px',
        },
        visible: {
            opacity: 1,
            y: '0px',
        },
    },
})) `
  color: ${brand.primary.alt};
  font-size: 18px;
  line-height: 23px;
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: ${fluid(16, 20)};

  svg {
    width: 14px;
    height: 14px;
    transform: rotate(180deg);
  }
`;
const HoverEffect = styled.div `
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 1;
  background: #66990033;
  opacity: 0;
  width: 100%;
  height: 100%;

  &[data-active='true'] {
    opacity: 1;
  }

  transition: opacity 0.25s ease-in-out;
`;
const LoadMore = styled.div `
  display: flex;
  justify-content: center;
  padding-bottom: 53px;

  ${ButtonStyles.Button} {
    min-width: 173px;
  }
`;
const DesktopFiltersWrapper = styled.div `
  display: none;

  @media ${from(Device.TabletLarge)} {
    display: contents;
  }
`;
const MobileFiltersWrapper = styled.div `
  display: none;

  @media ${until(Device.TabletLarge)} {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
`;
const MobileFiltersContainer = styled(m.div).attrs(() => ({
    initial: 'hidden',
    animate: 'visible',
    exit: 'hidden',
    variants: {
        hidden: {
            opacity: 0,
            x: '100%',
        },
        visible: {
            opacity: 1,
            x: '0%',
        },
    },
    transition: {
        bounce: 0,
        ease: 'easeInOut',
        duration: 0.5,
    },
})) `
  position: fixed;
  z-index: 100; // Sit above 'enquire now' button //
  background: rgba(0, 0, 0, 0.8);
  inset: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: flex-end;
`;
const MobileFiltersInner = styled.div `
  height: 100%;
  max-height: 100dvh;
  overflow: hidden;
  width: 100%;
  background: ${brand.white};
  display: flex;
  flex-direction: column;
  padding: 21px 16px;
  max-width: 80dvw;

  > span {
    ${fonts.DaxPro.Bold};
    font-size: 18px;
    line-height: 24px;
    margin-bottom: 13px;
    display: flex;
    align-items: center;
    gap: 7px;

    &[data-is-back] {
      cursor: pointer;
    }

    svg {
      width: 14px;
      height: 14px;
    }
  }
`;
const MobileFilterSection = styled.div `
  padding: 16px 0px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.3);
  cursor: pointer;

  svg {
    transform: rotate(180deg);
    width: 14px;
    height: 14px;
  }

  span {
    ${fonts.DaxPro.Medium}
    font-size: 18px;
    line-height: 22px;
  }

  &:first-child {
    padding-top: 0px;
  }

  &:last-child {
    border-bottom: none;
  }
`;
const MobileButtons = styled.div `
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: auto;

  ${ButtonStyles.Button} {
    width: 100%;

    &:last-child {
      ${ButtonStyles.Inner} {
        border-color: ${brand.primary.alt};
        color: ${brand.primary.alt};
      }
    }
  }
`;
const MobileFilterOption = styled.div `
  padding: 16px 0px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.3);
  cursor: pointer;

  &[data-disabled='true'] {
    opacity: 0.75;
    pointer-events: none;
  }

  span {
    ${fonts.DaxPro.Medium};
    font-size: 18px;
    line-height: 23px;
  }

  div {
    border: 1px solid ${brand.primary.alt};
    cursor: pointer;
    width: 24px;
    height: 24px;
    display: flex;
    justify-content: center;
    align-items: center;

    svg {
      width: 16px;
      height: 16px;
    }

    &[data-selected='true'] {
      background: ${brand.primary.alt};

      svg {
        opacity: 1;
      }
    }
  }
`;
const MobileCloseButton = styled.div `
  position: absolute;
  inset: 14px;
  color: ${brand.white};
  cursor: pointer;
  width: 20px;
  height: 20px;

  svg {
    width: 19px;
    height: 19px;
  }
`;
const MobileFilterSections = styled.div `
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;
const MobilePagination = styled.div `
  padding: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  ${fonts.DaxPro.Medium};
  font-size: 18px;
  line-height: 23px;
  text-align: center;
  color: ${brand.black};
  background-color: ${brand.grey.grey89};
  margin-left: calc(var(--gridPadding) * -1);
  width: calc(100% + var(--gridPadding) * 2);

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const NoResultsMsg = styled.div `
  grid-column: span 12;
  padding-top: 30px;

  @media ${from(Device.TabletLarge)} {
    text-align: center;
  }
`;
export const NewsListingStyles = {
    Container,
    TitleContainer,
    FiltersContainer,
    FiltersControls,
    FiltersLeft,
    MultiSelect,
    MultiSelectWrapper,
    MultiSelectOptions,
    MultiSelectCheck,
    Pills,
    Pill,
    FiltersRight,
    Cards,
    CardWrapper,
    ImageWrapper,
    LazyImage,
    SpinnerContainer,
    Date,
    CardContent,
    CardContentTitle,
    CardContentIntro,
    CardInner,
    CardContentCta,
    CardContentLine,
    HoverEffect,
    LoadMore,
    DesktopFiltersWrapper,
    MobileFiltersWrapper,
    MobileFiltersContainer,
    MobileFiltersInner,
    MobileFilterSection,
    MobileButtons,
    MobileFilterOption,
    MobileFilterSections,
    MobileCloseButton,
    MobilePagination,
    NoResultsMsg,
};
